import React, { useEffect, useRef, useState } from "react";
import { Zap, Activity, Check } from "lucide-react";

// Animated visualization: 25min EMS vs 4h classic sport — synchronized loop.
// Both start together; 25min finishes early ("Terminé"), 4h keeps going.
const LOOP_SEC = 16;

export default function TimerAnimation() {
  return (
    <div className="relative bg-gradient-to-br from-fegreen-light via-white to-white rounded-2xl sm:rounded-3xl p-6 sm:p-10 lg:p-12 max-w-2xl mx-auto lg:mx-0 shadow-[0_20px_60px_rgba(132,195,66,0.18)] border border-fegreen/20">
      <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-fegreen text-white text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] shadow-lg whitespace-nowrap">
        Même résultat
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr] gap-3 sm:gap-6 items-center pt-2">
        {/* EMS — 25min */}
        <Timer
          label="EMS à domicile"
          icon={Zap}
          target={25}
          unit="min"
          color="#84C342"
          trackColor="#E8F5DA"
          gradStops={["#B7E66E", "#84C342", "#5E9A28"]}
          fillUntil={9.4}
          highlight
          completedBadge
          testid="timer-ems"
        />

        {/* Equals */}
        <div className="flex flex-col items-center gap-1">
          <div className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-fegreen leading-none">=</div>
          <div className="text-[8px] sm:text-[10px] uppercase tracking-[0.2em] text-fegray font-bold">soit</div>
          <div className="text-xs sm:text-sm font-bold text-fegreen">9,6×</div>
          <div className="text-[8px] sm:text-[10px] uppercase tracking-wider text-fegray hidden sm:block">
            + rapide
          </div>
        </div>

        {/* Sport classique — 4h */}
        <Timer
          label="Sport classique"
          icon={Activity}
          target={4}
          unit="h"
          color="#6B7280"
          trackColor="#E5E7EB"
          gradStops={["#9CA3AF", "#6B7280", "#374151"]}
          fillUntil={94}
          testid="timer-sport"
        />
      </div>

      <p className="text-center text-xs sm:text-sm text-fegray mt-7 sm:mt-8 leading-relaxed px-2">
        Pendant qu&apos;une séance EMS se termine en 25 min, il faut encore{" "}
        <span className="font-bold text-fegreen">3h35</span> de sport classique
        pour les mêmes bénéfices.
      </p>
    </div>
  );
}

function Timer({ label, icon: Icon, target, unit, color, trackColor, gradStops, fillUntil, highlight, completedBadge, testid }) {
  const R = 56;
  const C = 2 * Math.PI * R;
  const fillKey = `dash-${testid}`;
  const tickKey = `tick-${testid}`;
  const badgeKey = `badge-${testid}`;

  // Animated counter — sync with the same 16s loop
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    const step = (t) => {
      if (startRef.current === null) startRef.current = t;
      const elapsed = ((t - startRef.current) / 1000) % LOOP_SEC;
      const progressPct = (elapsed / LOOP_SEC) * 100;
      let display;
      if (progressPct <= fillUntil) {
        display = (progressPct / fillUntil) * target;
      } else if (progressPct < 99) {
        display = target;
      } else {
        display = 0; // reset
      }
      // Integer for h (4), one-decimal-ish then floor for min (25)
      setValue(unit === "h" ? Math.min(target, Math.floor(display)) : Math.min(target, Math.round(display)));
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [fillUntil, target, unit]);

  return (
    <div className="flex flex-col items-center" data-testid={testid}>
      <style>{`
        @keyframes ${fillKey} {
          0%             { stroke-dashoffset: ${C}; }
          ${fillUntil}%  { stroke-dashoffset: 0; }
          99%            { stroke-dashoffset: 0; }
          100%           { stroke-dashoffset: ${C}; }
        }
        @keyframes ${tickKey} {
          0%             { transform: rotate(-90deg); }
          ${fillUntil}%  { transform: rotate(270deg); }
          99%            { transform: rotate(270deg); }
          100%           { transform: rotate(-90deg); }
        }
        @keyframes ${badgeKey} {
          0%, ${Math.max(0, fillUntil - 1)}% { opacity: 0; transform: scale(0.4); }
          ${fillUntil}%                       { opacity: 1; transform: scale(1); }
          99%                                  { opacity: 1; transform: scale(1); }
          100%                                 { opacity: 0; transform: scale(0.4); }
        }
        .${fillKey}-circle {
          stroke-dasharray: ${C};
          stroke-dashoffset: ${C};
          animation: ${fillKey} ${LOOP_SEC}s linear infinite;
          transform-origin: center;
          transform: rotate(-90deg);
        }
        .${tickKey}-needle {
          transform-origin: 70px 70px;
          animation: ${tickKey} ${LOOP_SEC}s linear infinite;
        }
        .${badgeKey}-mark {
          animation: ${badgeKey} ${LOOP_SEC}s ease-in-out infinite;
          opacity: 0;
        }
      `}</style>

      <div className="relative">
        <svg viewBox="0 0 140 140" className="w-28 h-28 sm:w-36 sm:h-36 lg:w-44 lg:h-44">
          <defs>
            <linearGradient id={`grad-${testid}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={gradStops[0]} />
              <stop offset="50%" stopColor={gradStops[1]} />
              <stop offset="100%" stopColor={gradStops[2]} />
            </linearGradient>
            <radialGradient id={`glow-${testid}`} cx="50%" cy="50%" r="50%">
              <stop offset="60%" stopColor={gradStops[1]} stopOpacity="0" />
              <stop offset="100%" stopColor={gradStops[1]} stopOpacity="0.25" />
            </radialGradient>
            <filter id={`shadow-${testid}`} x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feFlood floodColor={gradStops[1]} floodOpacity="0.4" />
              <feComposite in2="blur" operator="in" />
              <feMerge>
                <feMergeNode />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Outer halo */}
          <circle cx="70" cy="70" r="68" fill={`url(#glow-${testid})`} />

          {/* Track */}
          <circle cx="70" cy="70" r={R} stroke={trackColor} strokeWidth="10" fill="none" />

          {/* Progress with gradient */}
          <circle
            cx="70"
            cy="70"
            r={R}
            stroke={`url(#grad-${testid})`}
            strokeWidth="10"
            strokeLinecap="round"
            fill="none"
            filter={`url(#shadow-${testid})`}
            className={`${fillKey}-circle`}
          />

          {/* Needle — short tick at outer edge */}
          <g className={`${tickKey}-needle`}>
            <line
              x1="70"
              y1="16"
              x2="70"
              y2="30"
              stroke={gradStops[2]}
              strokeWidth="4"
              strokeLinecap="round"
            />
            <circle cx="70" cy="14" r="4.5" fill={gradStops[2]} />
          </g>
        </svg>

        {/* Center counter (icon + animated number) */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <Icon
            className="w-4 h-4 sm:w-5 sm:h-5 mb-1"
            style={{ color }}
            strokeWidth={2}
            fill={highlight ? color : "none"}
          />
          <div
            className="text-2xl sm:text-3xl lg:text-4xl font-extrabold leading-none tabular-nums"
            style={{ color: "#333" }}
            data-testid={`${testid}-counter`}
          >
            {value}
            <span className="text-sm sm:text-base ml-0.5" style={{ color }}>{unit}</span>
          </div>
        </div>

        {/* Completed badge — only on EMS */}
        {completedBadge && (
          <div
            className={`${badgeKey}-mark absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-fegreen text-white text-[9px] sm:text-[10px] font-bold uppercase tracking-wider shadow-lg flex items-center gap-1 whitespace-nowrap`}
          >
            <Check className="w-3 h-3" strokeWidth={3} /> Terminé
          </div>
        )}
      </div>

      <div className="text-center mt-5 sm:mt-6">
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.15em] font-bold" style={{ color }}>
          {label}
        </div>
        <div className="text-[9px] sm:text-[10px] text-fegray mt-1">
          {highlight ? "Intensité maximale" : "Intensité modérée"}
        </div>
      </div>
    </div>
  );
}
